import type { I18nLocale } from "@/modules/i18n/types"
import type { Location } from "@/modules/locations/locations"

export const DEFAULT_USER_ROLE = ["anonymous"] as const

export const STUDENT_ROLES = ["student", "student_recruiter"] as const

export const JT_ADMIN_ROLES = ["jt_admin", "jt_super_admin", "jt_dev_admin", "jt_support_admin"] as const

export const SCHOOL_ADMIN_ROLES = [
  "cc_admin",
  "cc_super_admin",
  "cc_content_manager",
  "cc_career_advisor_admin",
  "cc_companies_content_manager",
  "cc_school_content_manager",
  "cc_career_advisor",
  "cc_company_relationship_officer",
] as const

export const COMPANY_ADMIN_ROLES = [
  "company_admin",
  "company_offers_manager",
  "company_super_admin",
  "company_talent_bank_user",
] as const

// public_recruiter is the role for private recruiters, don't ask me why
export const RECRUITER_ADMIN_ROLES = ["public_recruiter", "company_school_admin"] as const

export const USER_ADMIN_ROLES = [...JT_ADMIN_ROLES, ...SCHOOL_ADMIN_ROLES, ...COMPANY_ADMIN_ROLES] as const

// Users eligible to apply to a JobAd
export const USER_ROLES_ELIGIBLE_TO_APPLY = [
  ...STUDENT_ROLES,
  "cc_admin",
  "cc_super_admin",
  "cc_content_manager",
  "cc_career_advisor",
  "cc_school_content_manager",
  "cc_company_relationship_officer",
  "cc_companies_content_manager",
]

// Users NOT eligible to apply to a JobAd
export const USER_ROLES_NOT_ELIGIBLE_TO_APPLY = [
  ...DEFAULT_USER_ROLE,
  ...STUDENT_ROLES,
  ...JT_ADMIN_ROLES,
  ...SCHOOL_ADMIN_ROLES,
  ...COMPANY_ADMIN_ROLES,
  ...RECRUITER_ADMIN_ROLES,
  ...USER_ADMIN_ROLES,
].filter(role => !USER_ROLES_ELIGIBLE_TO_APPLY.includes(role))

type DefaultUserRoles = (typeof DEFAULT_USER_ROLE)[number]

export type StudentRoles = (typeof STUDENT_ROLES)[number]

type JtAdminRoles = (typeof JT_ADMIN_ROLES)[number]

type SchoolAdminRoles = (typeof SCHOOL_ADMIN_ROLES)[number]

type CompanyAdminRoles = (typeof COMPANY_ADMIN_ROLES)[number]

type RecruiterAdminRoles = (typeof RECRUITER_ADMIN_ROLES)[number]

export type CurrentUserRole =
  | DefaultUserRoles
  | StudentRoles
  | JtAdminRoles
  | SchoolAdminRoles
  | CompanyAdminRoles
  | RecruiterAdminRoles

export type UserAdminRoles = (typeof USER_ADMIN_ROLES)[number]

export type UserRolesEligibleToApply = (typeof USER_ROLES_ELIGIBLE_TO_APPLY)[number]

export type CurrentUserCurriculumIds = null | string[]

type CurrentUserAttributes = {
  anonymous: boolean
  anyCompanyRole: boolean
  avatarUrl: string | null
  careerCenterId: string | null
  careerCenterShortName: string | null
  careerCenterUuid: string | null
  companyPermalink: string | null
  companyUuid: string | null
  country: string | null
  curriculumIds: CurrentUserCurriculumIds
  dailyJobOffersAlert: boolean | null
  email: string | null
  familyName: string | null
  firstName: string | null
  givenName: string | null
  hasAlumniCurriculum: boolean
  hasCommunityDiscussions: boolean
  hasCommunityInsights: boolean
  hasCommunityModule: boolean
  hasExclusiveOffers: boolean
  hasSchoolProfile: boolean | null
  isAdmin: boolean | null
  isAlumni: boolean | null
  isCcSuperAdmin: boolean | null
  isHiddenAdmin: boolean | null
  isStudent: boolean | null
  isWithCompanySpace: boolean | null
  jwtToken: string
  lastName: string | null
  lastSignInAt: string | null
  locales?: I18nLocale[]
  recruiter: boolean | null
  roleTitle: CurrentUserRole
  shouldRedirectToOnboarding: boolean | null
  schoolId: number | null
  showJobRoles: boolean | null
  uuid: string | null
}

type CurrentUserRelationShips = {
  careerCenter: {
    data: {
      id: string
      type: string
    }
  }
}

export type CurrentUser = {
  id: string
  isLogged: boolean
  type: string
  attributes: CurrentUserAttributes
  relationships: CurrentUserRelationShips
}

export type CurrentUserResponse = {
  data: CurrentUser
}

export type SearchConfigFilterOption = {
  label: string
  name: string | number
  selected?: boolean
  highlighted?: boolean
}

export type SearchConfigFilterOptionWithChildren = {
  label: string
  name: string
  selected?: boolean
  children: SearchConfigFilterOption[]
}

export type SearchConfigFilter = {
  label: string
  name: string
  options: SearchConfigFilterOption[] | SearchConfigFilterOptionWithChildren[]
}

export type CurrentUserSearchConfig = {
  data: {
    relationships: {
      searchFilters: {
        data: SearchConfigFilter[]
      }
    }
  }
}

export type CurrentUserSearchCriteria = {
  abroadOnly: boolean
  alumni: boolean
  companyBusinessTypes: string[]
  companySectors: string[]
  contractDurations: string[]
  contractTypes: string[]
  curriculumIds: string[]
  locale: I18nLocale[]
  locations: Location[]
  positionCategoryIds: string[]
  remoteTypes: string[]
  schoolId: string
  studyLevels: number[]
  tags: string[]
  workExperienceCodes: string[]
}

export type CurrentUserSearchCriteriaResponse = {
  searchCriteria: CurrentUserSearchCriteria
}
